import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Download, Clock, Loader2, AlertCircle, Shield, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Buyer download recovery page.
 * Reached via the "Re-open your downloads" button in the order confirmation email.
 *
 * The backend endpoint `/api/orders/recover/{token}`:
 *  - Refuses after 24h (410) or after 5 uses (429)
 *  - Increments a use counter atomically per call
 *  - Returns the order's session_id so this browser can hit the normal
 *    /api/download/{token}/file endpoints (which are session-scoped)
 */
const OrderRecoverPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'loading', data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/orders/recover/${token}`);
        if (cancelled) return;
        // Rehydrate the original session so /api/download/... endpoints authorize
        if (data.session_id) {
          localStorage.setItem('session_id', data.session_id);
        }
        setState({ status: 'ready', data, error: null });
      } catch (err) {
        if (cancelled) return;
        const code = err?.response?.status;
        const detail = err?.response?.data?.detail || 'Recovery link is not usable.';
        setState({ status: 'error', data: null, error: { code, detail } });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const triggerBrowserDownload = (url) => {
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDownload = async (dlToken) => {
    const sessionId = localStorage.getItem('session_id');
    try {
      const { data } = await axios.get(`${API}/download/${dlToken}?session_id=${encodeURIComponent(sessionId)}`);
      const usableTracks = data.tracks.filter((t) => t.has_file);
      if (usableTracks.length === 0) {
        alert('No downloadable files are attached to this release. Please contact support.');
        return;
      }
      const base = `${API}/download/${dlToken}`;
      const q = `session_id=${encodeURIComponent(sessionId)}`;
      if (usableTracks.length === 1) {
        triggerBrowserDownload(`${base}/file?${q}&track=${usableTracks[0].index}`);
      } else {
        triggerBrowserDownload(`${base}/zip?${q}`);
      }
    } catch (error) {
      if (error.response?.status === 410) alert('This download link has expired.');
      else if (error.response?.status === 404) alert('Download link no longer valid.');
      else alert('Download failed. Please try again in a moment.');
    }
  };

  if (state.status === 'loading') {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-4" data-testid="recover-loading">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 text-[#00F0FF] animate-spin" size={40} />
          <p className="text-[#A1A1AA] text-sm">Restoring your downloads…</p>
        </div>
      </div>
    );
  }

  if (state.status === 'error') {
    const { code, detail } = state.error || {};
    const isExpired = code === 410;
    const isBurned = code === 429;
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-4" data-testid="recover-error">
        <div className="max-w-md w-full bg-[#121214] border border-[#27272A] rounded-lg p-8 text-center">
          <AlertCircle className="mx-auto mb-4 text-yellow-400" size={48} />
          <h1 className="text-xl font-semibold mb-2">
            {isExpired ? 'Recovery link expired' : isBurned ? 'Recovery link used up' : 'Recovery link not usable'}
          </h1>
          <p className="text-sm text-[#A1A1AA] mb-6">{detail}</p>
          <Button
            onClick={() => navigate('/contact')}
            className="w-full bg-[#00F0FF] hover:bg-[#33F3FF] text-black rounded-full"
            data-testid="recover-contact-support"
          >
            Contact support
          </Button>
          <button
            onClick={() => navigate('/')}
            className="mt-4 text-xs text-[#71717A] hover:text-[#A1A1AA] inline-flex items-center gap-1"
          >
            <ArrowLeft size={12} /> Back to store
          </button>
        </div>
      </div>
    );
  }

  const { data } = state;
  const remaining = data?.uses_remaining ?? 0;

  return (
    <div className="min-h-screen bg-[#09090B] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-[#121214] border border-[#27272A] rounded-lg p-8" data-testid="recover-container">
          <div className="text-center mb-8">
            <Shield className="mx-auto mb-3 text-[#00F0FF]" size={48} />
            <h1 className="text-3xl font-heading font-bold mb-2">Welcome back</h1>
            <p className="text-[#A1A1AA] text-sm">Your downloads are ready — same 24-hour window from your purchase.</p>
          </div>

          <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-4 mb-6 flex items-start gap-3" data-testid="recover-usage-banner">
            <Clock className="text-[#00F0FF] flex-shrink-0 mt-0.5" size={20} />
            <div className="text-sm">
              <p className="font-medium mb-1">
                {remaining > 0
                  ? `${remaining} recovery ${remaining === 1 ? 'open' : 'opens'} left after this one`
                  : 'This is your last recovery open — download everything now.'}
              </p>
              <p className="text-[#A1A1AA] text-xs">Do not share this link. Each open counts toward the limit, and it expires 24h after purchase.</p>
            </div>
          </div>

          <div className="space-y-3">
            {(data?.downloads || []).map((dl) => (
              <div
                key={dl.id || dl.token}
                className="flex items-center justify-between p-4 bg-[#18181B] rounded-lg"
                data-testid={`recover-download-${dl.album_id}`}
              >
                <div>
                  <div className="font-medium">Album</div>
                  <div className="text-sm text-[#A1A1AA] font-mono">{dl.album_id?.slice(0, 8)}…</div>
                </div>
                <Button
                  onClick={() => handleDownload(dl.token)}
                  className="bg-[#00F0FF] hover:bg-[#33F3FF] text-black rounded-full"
                  data-testid={`recover-download-btn-${dl.album_id}`}
                >
                  <Download size={16} className="mr-2" /> Download
                </Button>
              </div>
            ))}
            {(!data?.downloads || data.downloads.length === 0) && (
              <p className="text-center text-sm text-[#71717A] py-8">
                No active downloads found for this order.
              </p>
            )}
          </div>

          <div className="pt-6 mt-6 border-t border-[#27272A]">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="w-full border-[#27272A] hover:bg-[#18181B] rounded-full"
              data-testid="recover-back-to-store"
            >
              Back to store
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderRecoverPage;
